import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, Brain, Briefcase, DollarSign, Lightbulb, Loader2, Plus, RefreshCw, Rocket, ShieldCheck, Sparkles, Wrench, X } from 'lucide-react';

const LANE_LABELS = {
  backlog: 'Backlog',
  en_progreso: 'En progreso',
  en_revision: 'En revisión',
  aprobado: 'Aprobado',
  publicado: 'Publicado',
  aprendizajes: 'Aprendizajes',
};

const TYPE_CONFIG = {
  orden: { label: 'Orden', icon: Briefcase, color: 'bg-primary/15 text-primary border-primary/30' },
  roi: { label: 'ROI', icon: DollarSign, color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  trabajo: { label: 'Trabajo', icon: Wrench, color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  aprendizaje: { label: 'Aprendizaje', icon: Brain, color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  servicio: { label: 'Servicio', icon: Sparkles, color: 'bg-pink-500/15 text-pink-400 border-pink-500/30' },
  tecnologia: { label: 'Tecnología', icon: Lightbulb, color: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
};

const PRIORITY_STYLES = {
  baja: 'text-gray-400 border-gray-500/30',
  media: 'text-blue-400 border-blue-500/30',
  alta: 'text-orange-400 border-orange-500/30',
  urgente: 'text-red-400 border-red-500/30',
};

const metricCards = (metrics) => [
  { label: 'Tarjetas', value: metrics.cards_total, icon: BarChart3, color: 'bg-primary/15 text-primary' },
  { label: 'ROI Esperado', value: `$${metrics.roi_expected_total}`, icon: DollarSign, color: 'bg-green-500/15 text-green-400' },
  { label: 'Coste Estimado', value: `$${metrics.roi_cost_total}`, icon: ShieldCheck, color: 'bg-red-500/15 text-red-400' },
  { label: 'Horas', value: metrics.hours_total, icon: Brain, color: 'bg-yellow-500/15 text-yellow-400' },
  { label: 'Campañas listas', value: metrics.social_orders_ready, icon: Rocket, color: 'bg-indigo-500/15 text-indigo-300' },
  { label: 'Órdenes IA activas', value: metrics.tasks_in_progress, icon: Wrench, color: 'bg-blue-500/15 text-blue-400' },
];

export default function CommandCenterBoard({ data, onRefresh, onSeedLaunch, onCreateCard, onUpdateCard }) {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCard, setNewCard] = useState({
    title: '', description: '', lane: 'backlog', item_type: 'orden', owner: '', priority: 'media',
    estimated_cost: 0, expected_revenue: 0, roi_percent: 0, hours_estimate: 0, related_source: 'main_command_center', tags: [], notes: ''
  });

  const cardsByLane = useMemo(() => {
    const map = {};
    data?.lanes?.forEach((lane) => { map[lane] = []; });
    (data?.cards || []).forEach((card) => {
      if (!map[card.lane]) map[card.lane] = [];
      map[card.lane].push(card);
    });
    return map;
  }, [data]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onCreateCard({ ...newCard, tags: newCard.tags.filter(Boolean) });
    setSaving(false);
    setShowCreate(false);
    setNewCard({
      title: '', description: '', lane: 'backlog', item_type: 'orden', owner: '', priority: 'media',
      estimated_cost: 0, expected_revenue: 0, roi_percent: 0, hours_estimate: 0, related_source: 'main_command_center', tags: [], notes: ''
    });
  };

  const moveCard = async (card, lane) => {
    setSaving(true);
    await onUpdateCard(card.id, { lane });
    setSaving(false);
  };

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="command-center-board">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold">Centro de Mando</h3>
          <p className="text-text-muted text-sm mt-1">Órdenes, métricas, ROI, trabajos asignados, aprendizajes, servicios y tecnologías en un único tablero.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onRefresh} className="p-2 rounded-lg bg-card border border-border-subtle text-text-muted hover:text-primary transition-all" data-testid="refresh-command-center">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={onSeedLaunch} className="btn-secondary text-sm py-2 px-4" data-testid="seed-launch-master">
            <Rocket className="w-4 h-4" /> Preparar lanzamiento
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm py-2 px-4" data-testid="create-command-card-btn">
            <Plus className="w-4 h-4" /> Nueva tarjeta
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
        {metricCards(data.metrics).map((item) => (
          <div key={item.label} className="glass-card p-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${item.color}`}><item.icon className="w-5 h-5" /></div>
            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">{item.label}</p>
            <p className="text-text-heading font-heading text-xl font-semibold">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="glass-card p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div>
            <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Pulso de lanzamiento</p>
            <h4 className="text-text-heading font-heading font-semibold text-lg">Campañas + órdenes + ejecución</h4>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {(data.social_orders || []).slice(0, 3).map((order) => (
              <span key={order.id} className="px-2 py-1 rounded-full bg-card border border-border-subtle text-text-body">{order.campaign_name}</span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 overflow-x-auto">
          {data.lanes.map((lane) => (
            <div key={lane} className="min-w-[250px] space-y-3" data-testid={`command-lane-${lane}`}>
              <div className="flex items-center justify-between px-1">
                <p className="text-text-heading font-heading font-semibold text-sm">{LANE_LABELS[lane]}</p>
                <span className="text-text-muted text-xs">{(cardsByLane[lane] || []).length}</span>
              </div>
              <div className="space-y-3 min-h-[160px]">
                {(cardsByLane[lane] || []).map((card) => {
                  const typeCfg = TYPE_CONFIG[card.item_type] || TYPE_CONFIG.orden;
                  const TypeIcon = typeCfg.icon;
                  return (
                    <motion.button key={card.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      onClick={() => setSelectedCard(card)}
                      className="w-full text-left glass-card p-4 hover:border-primary/40 transition-all" data-testid={`command-card-${card.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border ${typeCfg.color}`}>
                          <TypeIcon className="w-3 h-3" /> {typeCfg.label}
                        </span>
                        <span className={`text-[10px] px-2 py-1 rounded-full border ${PRIORITY_STYLES[card.priority] || PRIORITY_STYLES.media}`}>{card.priority}</span>
                      </div>
                      <h5 className="text-text-heading font-medium text-sm mb-2 line-clamp-2">{card.title}</h5>
                      <p className="text-text-muted text-xs line-clamp-3 mb-3">{card.description}</p>
                      <div className="grid grid-cols-2 gap-2 text-[10px] text-text-muted">
                        <span>Owner: {card.owner || '—'}</span>
                        <span>ROI: {card.roi_percent || 0}%</span>
                        <span>Coste: ${card.estimated_cost || 0}</span>
                        <span>Revenue: ${card.expected_revenue || 0}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-command-card-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-text-heading font-heading font-semibold text-lg">Nueva tarjeta del Centro de Mando</h3>
                <button onClick={() => setShowCreate(false)} className="text-text-muted hover:text-white p-1"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submitCreate} className="space-y-4">
                <input className="input-kinesthetic" placeholder="Título" value={newCard.title} onChange={(e) => setNewCard({ ...newCard, title: e.target.value })} required data-testid="command-card-title" />
                <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Descripción" value={newCard.description} onChange={(e) => setNewCard({ ...newCard, description: e.target.value })} required data-testid="command-card-description" />
                <div className="grid md:grid-cols-3 gap-3">
                  <select className="input-kinesthetic" value={newCard.lane} onChange={(e) => setNewCard({ ...newCard, lane: e.target.value })} data-testid="command-card-lane">
                    {data.lanes.map((lane) => <option key={lane} value={lane}>{LANE_LABELS[lane]}</option>)}
                  </select>
                  <select className="input-kinesthetic" value={newCard.item_type} onChange={(e) => setNewCard({ ...newCard, item_type: e.target.value })} data-testid="command-card-type">
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
                  </select>
                  <select className="input-kinesthetic" value={newCard.priority} onChange={(e) => setNewCard({ ...newCard, priority: e.target.value })} data-testid="command-card-priority">
                    {['baja','media','alta','urgente'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input className="input-kinesthetic" placeholder="Owner / agente responsable" value={newCard.owner} onChange={(e) => setNewCard({ ...newCard, owner: e.target.value })} data-testid="command-card-owner" />
                  <input className="input-kinesthetic" placeholder="Fuente relacionada" value={newCard.related_source} onChange={(e) => setNewCard({ ...newCard, related_source: e.target.value })} data-testid="command-card-source" />
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <input type="number" className="input-kinesthetic" placeholder="Coste" value={newCard.estimated_cost} onChange={(e) => setNewCard({ ...newCard, estimated_cost: Number(e.target.value) })} data-testid="command-card-cost" />
                  <input type="number" className="input-kinesthetic" placeholder="Revenue" value={newCard.expected_revenue} onChange={(e) => setNewCard({ ...newCard, expected_revenue: Number(e.target.value) })} data-testid="command-card-revenue" />
                  <input type="number" className="input-kinesthetic" placeholder="ROI %" value={newCard.roi_percent} onChange={(e) => setNewCard({ ...newCard, roi_percent: Number(e.target.value) })} data-testid="command-card-roi" />
                  <input type="number" className="input-kinesthetic" placeholder="Horas" value={newCard.hours_estimate} onChange={(e) => setNewCard({ ...newCard, hours_estimate: Number(e.target.value) })} data-testid="command-card-hours" />
                </div>
                <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas" value={newCard.notes} onChange={(e) => setNewCard({ ...newCard, notes: e.target.value })} data-testid="command-card-notes" />
                <button type="submit" className="btn-primary w-full" disabled={saving} data-testid="submit-command-card">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : <><Plus className="w-4 h-4" /> Crear tarjeta</>}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedCard && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={() => setSelectedCard(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="glass-card p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="command-card-detail-modal">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-text-muted text-xs uppercase tracking-wider mb-1">{TYPE_CONFIG[selectedCard.item_type]?.label || selectedCard.item_type}</p>
                  <h3 className="text-text-heading font-heading font-semibold text-lg">{selectedCard.title}</h3>
                </div>
                <button onClick={() => setSelectedCard(null)} className="text-text-muted hover:text-white p-1"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-text-body whitespace-pre-wrap mb-4">{selectedCard.description}</p>
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div className="bg-card rounded-xl p-3 border border-border-subtle"><p className="text-text-muted text-xs mb-1">Owner</p><p className="text-text-heading">{selectedCard.owner || '—'}</p></div>
                <div className="bg-card rounded-xl p-3 border border-border-subtle"><p className="text-text-muted text-xs mb-1">ROI</p><p className="text-text-heading">{selectedCard.roi_percent || 0}%</p></div>
                <div className="bg-card rounded-xl p-3 border border-border-subtle"><p className="text-text-muted text-xs mb-1">Coste</p><p className="text-text-heading">${selectedCard.estimated_cost || 0}</p></div>
                <div className="bg-card rounded-xl p-3 border border-border-subtle"><p className="text-text-muted text-xs mb-1">Revenue</p><p className="text-text-heading">${selectedCard.expected_revenue || 0}</p></div>
              </div>
              <div className="space-y-3">
                <p className="text-text-muted text-xs uppercase tracking-wider">Mover a columna</p>
                <div className="flex flex-wrap gap-2">
                  {data.lanes.filter((lane) => lane !== selectedCard.lane).map((lane) => (
                    <button key={lane} onClick={() => moveCard(selectedCard, lane)} className="px-3 py-2 rounded-lg text-sm bg-card border border-border-subtle text-text-body hover:border-primary/40 transition-all" data-testid={`move-card-${lane}`}>
                      {LANE_LABELS[lane]}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}